import React, { useState, useEffect } from 'react';

// Core components
import URLInput from './components/core/URLInput';
import Dashboard from './components/core/Dashboard';
import AnalysisProgress from './components/core/AnalysisProgress';
import MultiUrlProgress from './components/core/MultiUrlProgress';
import SavedAnalysesModal from './components/core/SavedAnalysesModal';
import SaveAnalysisButton from './components/core/SaveAnalysisButton';
import AdminLogin from './components/core/AdminLogin';
import IntroPage from './components/core/IntroPage';

// V2 components
import DashboardV2 from './components/v2/DashboardV2';
import AppV2 from './components/v2/AppV2';

// Pages
import HallOfFameReport from './pages/HallOfFameReport';
import HallOfFameReportV2 from './pages/HallOfFameReportV2';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { analyzePageSpeed, disconnectWebSocket, analyzeHtml, analyzeComplete, checkBackendConnection, connectWebSocket } from './services/api';
import { ServiceData, PageSpeedResult } from './types';
import { SavedAnalysis } from './utils/analysisStorage';
import { calculateMobileRating, calculateDesktopRating, getRatingGrade, getRatingColor, SiteData } from './utils/overallRating';
import './index.css';

interface AppState {
  isLoading: boolean;
  isLlmLoading: boolean;
  data: ServiceData[] | null;
  pageSpeedData: PageSpeedResult | null;
  llmAnalysis: string | null;
  error: string | null;
  analyzedUrl: string | null;
  currentStep: number;
  totalSteps: number;
  stepName: string;
  sessionId: string | null;

  // 다중 URL 분석 관련 상태
  isBatchMode: boolean;
  batchId: string | null;
  batchResults: AnalysisResult[];
  selectedUrlIndex?: number;
  multiProgress: {
    completed: number;
    total: number;
    currentUrl?: string;
    currentIndex?: number;
    urls?: string[];
    urlProgress?: Array<{
      url: string;
      status: 'pending' | 'waiting' | 'analyzing' | 'completed' | 'error';
      message?: string;
      error?: string;
    }>;
  };

  // 백엔드 분석 결과의 추가 정보
  timestamp?: string;
  resourceSummary?: any;
  importantInfo?: any;
  customRatings?: { mobile: number; desktop: number; overall: number }; // V1.1 커스텀 성능 점수
}

interface AnalysisResult {
  url: string;
  batchId?: string;
  urlIndex?: number;
  isPrimary?: boolean;
  failed?: boolean;
  error?: string;
  data?: ServiceData[];
  services?: ServiceData[]; // /api/analyze-html 응답에서 사용
  pageSpeedData?: PageSpeedResult;
  llmAnalysis?: string;
  timestamp: string;
  // 백엔드에서 추가로 제공하는 상세 정보
  importantInfo?: any;
  resourceSummary?: any;
  resources?: any[];
}

// PageSpeedResult를 기반으로 자체 평점 계산하는 헬퍼 함수 (V1.1 비선형 스케일링)
const calculateCustomRating = (pageSpeedData: PageSpeedResult): number => {
  // Core Web Vitals 데이터를 추출하여 SiteData 형식으로 변환
  const siteData: SiteData = {
    fcp_desktop: pageSpeedData.desktop?.firstContentfulPaint || undefined,
    fcp_mobile: pageSpeedData.mobile?.firstContentfulPaint || undefined,
    lcp_desktop: pageSpeedData.desktop?.largestContentfulPaint || undefined,
    lcp_mobile: pageSpeedData.mobile?.largestContentfulPaint || undefined,
    tbt_desktop: pageSpeedData.desktop?.totalBlockingTime || undefined,
    tbt_mobile: pageSpeedData.mobile?.totalBlockingTime || undefined,
    total_size_desktop: pageSpeedData.desktop?.pageSize?.totalSize || undefined,
    total_size_mobile: pageSpeedData.mobile?.pageSize?.totalSize || undefined,
  };

  // 명예의전당 평균 데이터 (상대평가용 기준점)
  const benchmarkSites: SiteData[] = [
    // 실제 웹사이트 평균 성능 지표들 (기준점)
    { fcp_mobile: 2000, lcp_mobile: 4000, tbt_mobile: 300, total_size_mobile: 2000000 },
    { fcp_mobile: 1500, lcp_mobile: 2500, tbt_mobile: 100, total_size_mobile: 1000000 },
    { fcp_mobile: 3000, lcp_mobile: 6000, tbt_mobile: 600, total_size_mobile: 4000000 },
    { fcp_mobile: 2500, lcp_mobile: 3500, tbt_mobile: 200, total_size_mobile: 1500000 },
    { fcp_mobile: 1800, lcp_mobile: 3000, tbt_mobile: 150, total_size_mobile: 1200000 }
  ];

  // V1.1 비선형 스케일링 적용
  const mobileRating = calculateMobileRating(siteData, benchmarkSites);
  const desktopRating = calculateDesktopRating(siteData, benchmarkSites);

  return Math.round((mobileRating + desktopRating) / 2);
};

function AppContent() {
  const { isAdmin, user, isLoading, isGuestMode, enterGuestMode, canLoadAnalysis, canSaveAnalysis } = useAuth();
  const [showSavedAnalysesModal, setShowSavedAnalysesModal] = useState(false);
  
  // 인트로 화면 표시 여부: 로딩이 완료되고 사용자가 로그인하지 않았으며 게스트 모드도 아닌 경우
  const shouldShowIntro = !isLoading && !user && !isGuestMode;
  
  const [state, setState] = useState<AppState>({
    isLoading: false,
    isLlmLoading: false,
    data: null,
    pageSpeedData: null,
    llmAnalysis: null,
    error: null,
    analyzedUrl: null,
    currentStep: 0,
    totalSteps: 3,
    stepName: '',
    sessionId: null,

    // 다중 URL 분석 초기 상태
    isBatchMode: false,
    batchId: null,
    batchResults: [],
    multiProgress: {
      completed: 0,
      total: 0,
      currentUrl: undefined,
      currentIndex: undefined,
    },
  });

  // 컴포넌트 언마운트 시 WebSocket 정리
  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, []);

  const handleAnalyze = async (url: string) => {
    console.log('🚀 분석 시작:', url);
    console.log('🌐 현재 클라이언트 위치:', window.location.origin);
    
    setState(prev => ({
      ...prev,
      isLoading: true,
      isLlmLoading: true,
      error: null,
      data: null,
      pageSpeedData: null,
      llmAnalysis: null,
      currentStep: 0,
      totalSteps: 4,
      stepName: '백엔드 연결 상태 확인 중...',
      sessionId: `analysis-${Date.now()}`,
    }));

    try {
      // 0단계: 백엔드 연결 상태 확인
      console.log('🔍 백엔드 연결 상태 확인 시작');
      const isConnected = await checkBackendConnection();
      
      if (!isConnected) {
        throw new Error('백엔드 서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.');
      }

      setState(prev => ({
        ...prev,
        currentStep: 1,
        stepName: '페이지 성능 분석 및 AI 분석 중...'
      }));

      // 1단계: 완전한 분석 요청 (PageSpeed + LLM + 메타데이터)
      console.log('📊 완전한 분석 시작 (PageSpeed + LLM)');
      const completeAnalysis = await analyzeComplete(url);

      if (!completeAnalysis) {
        throw new Error('분석 결과를 가져올 수 없습니다.');
      }

      console.log('✅ 완전한 분석 완료:', completeAnalysis);

      setState(prev => ({
        ...prev,
        currentStep: 2,
        stepName: '최종 결과 정리 중...'
      }));

      // 2단계: 간단한 서비스 데이터 생성 (기존 테스트용 데이터 활용)
      const mockServices: ServiceData[] = [
        { name: 'Google Analytics', status: 'active', usage: true },
        { name: 'Facebook Pixel', status: 'inactive', usage: false },
        { name: 'jQuery', status: 'active', usage: true },
        { name: 'Bootstrap', status: 'inactive', usage: false },
        { name: 'Font Awesome', status: 'active', usage: true }
      ];

      console.log('🎉 모든 분석 완료');
      console.log('📊 커스텀 성능 점수:', completeAnalysis.customRatings);

      setState(prev => ({
        ...prev,
        isLoading: false,
        isLlmLoading: false,
        data: mockServices,
        pageSpeedData: completeAnalysis.pageSpeedData,
        llmAnalysis: completeAnalysis.llmAnalysis,
        analyzedUrl: url,
        error: null,
        currentStep: 4,
        stepName: '분석 완료!',
        // ✅ 백엔드에서 생성된 timestamp 및 메타데이터 사용
        timestamp: completeAnalysis.timestamp,
        resourceSummary: completeAnalysis.resourceSummary,
        importantInfo: completeAnalysis.importantInfo,
        customRatings: completeAnalysis.customRatings // V1.1 커스텀 성능 점수 추가
      }));
      
    } catch (error) {
      console.error('분석 중 오류 발생:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        isLlmLoading: false,
        error: '분석 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        data: null,
        pageSpeedData: null,
        llmAnalysis: null,
        currentStep: 0,
        stepName: '',
      }));
    }
  };

  // 다중 URL 분석 핸들러
  const handleMultiAnalyze = async (urls: string[]) => {
    console.log('🚀 다중 URL 분석 시작:', urls);

    const batchId = `batch-${Date.now()}`;
    const sessionId = `batch-analysis-${Date.now()}`;

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      isBatchMode: true,
      batchId: batchId,
      batchResults: [],
      multiProgress: {
        completed: 0,
        total: urls.length,
        currentUrl: urls[0],
        currentIndex: 0,
        urls: urls,
        urlProgress: urls.map((url, index) => ({
          url,
          status: index === 0 ? 'analyzing' : 'pending',
          message: index === 0 ? '분석 시작...' : '대기 중'
        }))
      },
      sessionId: sessionId,
      // 단일 분석 상태 초기화
      data: null,
      pageSpeedData: null,
      llmAnalysis: null,
      analyzedUrl: null,
    }));

    try {
      // WebSocket 연결 및 다중 URL 이벤트 리스너 설정 (sessionId 전달)
      const socket = await connectWebSocket(sessionId);

      socket.on('multiStarted', (data: any) => {
        console.log('📨 다중 URL 분석 시작 알림:', data);
      });

      socket.on('multiProgress', (data: any) => {
        console.log('📊 다중 URL 진행 상황:', data);
        setState(prev => {
          const newUrlProgress = prev.multiProgress.urlProgress?.slice() || [];

          // 현재 URL 상태 업데이트
          if (data.currentUrl && data.currentIndex !== undefined) {
            const urlIndex = data.currentIndex;
            if (newUrlProgress[urlIndex]) {
              newUrlProgress[urlIndex] = {
                ...newUrlProgress[urlIndex],
                status: data.status || 'analyzing',
                message: data.message || '분석 중...'
              };
            }
          }

          // 에러가 있으면 해당 URL에 에러 표시
          if (data.error && data.currentIndex !== undefined) {
            const urlIndex = data.currentIndex;
            if (newUrlProgress[urlIndex]) {
              newUrlProgress[urlIndex] = {
                ...newUrlProgress[urlIndex],
                status: 'error',
                error: data.error
              };
            }
          }

          return {
            ...prev,
            multiProgress: {
              ...prev.multiProgress,
              completed: data.completed,
              total: data.total,
              currentUrl: data.currentUrl,
              currentIndex: data.currentIndex,
              urlProgress: newUrlProgress
            },
          };
        });
      });

      socket.on('multiComplete', (data: any) => {
        console.log('✅ 다중 URL 분석 완료:', data);
        console.log('🔍 results 배열:', data.results);
        const results = data.results || [];

        setState(prev => ({
          ...prev,
          isLoading: false,
          batchResults: results,
          selectedUrlIndex: undefined, // 아무것도 선택하지 않음
          // Dashboard 데이터를 초기화 (보고서를 자동으로 표시하지 않음)
          data: null,
          pageSpeedData: null,
          llmAnalysis: null,
          analyzedUrl: null,
          multiProgress: {
            completed: data.total || results.length,
            total: data.total || results.length,
            currentUrl: undefined,
            currentIndex: undefined,
          },
        }));
      });

      socket.on('multiError', (data: any) => {
        console.error('❌ 다중 URL 분석 오류:', data);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: data.error || '동시 페이지 분석 중 오류가 발생했습니다.',
          multiProgress: {
            completed: 0,
            total: 0,
            currentUrl: undefined,
            currentIndex: undefined,
          },
        }));
      });

      // 백엔드에 다중 URL 분석 요청
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (user) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('google_auth_token')}`;
      }

      // BACKEND_URL을 가져와서 사용
      const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3300';
      const response = await fetch(`${BACKEND_URL}/api/batch-analyze`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          urls: urls,
          sessionId: sessionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '동시 페이지 분석 요청 실패');
      }

      console.log('📤 다중 URL 분석 요청 전송 완료');

    } catch (error) {
      console.error('다중 URL 분석 중 오류 발생:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '동시 페이지 분석 중 오류가 발생했습니다.',
        multiProgress: {
          completed: 0,
          total: 0,
          currentUrl: undefined,
          currentIndex: undefined,
        },
      }));
    }
  };

  // 저장된 분석을 불러오는 함수
  const handleLoadAnalysis = (analysis: SavedAnalysis) => {
    // ServiceData는 저장하지 않으므로 빈 배열로 설정 (대시보드 표시를 위해)
    const dummyServiceData: ServiceData[] = [];

    setState({
      isLoading: false,
      isLlmLoading: false,
      data: dummyServiceData,
      pageSpeedData: analysis.pageSpeedData,
      llmAnalysis: analysis.llmAnalysis,
      error: null,
      analyzedUrl: analysis.url,
      currentStep: 0,
      totalSteps: 3,
      stepName: '',
      sessionId: null,
      // 다중 URL 분석 상태 초기화
      isBatchMode: false,
      batchId: null,
      batchResults: [],
      multiProgress: {
        completed: 0,
        total: 0,
        currentUrl: undefined,
        currentIndex: undefined,
      },
      // 백엔드 상세 정보 (저장된 분석에서 가져오기)
      timestamp: analysis.timestamp,
      resourceSummary: undefined,
      importantInfo: undefined,
    });
  };

  const handleUrlClick = (url: string, index: number) => {
    console.log(`📊 URL 보고서 선택: ${url} (인덱스: ${index})`);
    const result = state.batchResults[index];
    if (result) {
      console.log(`🔍 선택된 결과 객체:`, result);
      console.log(`🔍 services:`, result.services);
      console.log(`🔍 pageSpeedData:`, result.pageSpeedData);
      console.log(`🔍 llmAnalysis:`, result.llmAnalysis);

      // /api/analyze-html 응답 구조에 맞게 데이터 매핑 (타입 안전성 확보)
      const services = result.services || (result as any).data?.services || null;
      const pageSpeedData = result.pageSpeedData || null;

      // llmAnalysis가 문자열인지 확인하고 안전하게 매핑
      let llmAnalysis = result.llmAnalysis || null;

      // 백엔드 상세 정보 추출
      const timestamp = result.timestamp || undefined;
      const resourceSummary = result.resourceSummary || undefined;
      const importantInfo = result.importantInfo || undefined;

      console.log(`🔍 매핑된 services:`, services);
      console.log(`🔍 매핑된 pageSpeedData:`, pageSpeedData);
      console.log(`🔍 매핑된 llmAnalysis:`, typeof llmAnalysis, llmAnalysis);
      console.log(`🔍 매핑된 timestamp:`, timestamp);
      console.log(`🔍 매핑된 resourceSummary:`, resourceSummary);
      console.log(`🔍 매핑된 importantInfo:`, importantInfo);

      setState(prev => ({
        ...prev,
        selectedUrlIndex: index,
        data: services,
        pageSpeedData: pageSpeedData,
        llmAnalysis: llmAnalysis,
        analyzedUrl: result.url || url,
        isBatchMode: false, // Dashboard를 표시하기 위해 배치 모드 해제
        // 백엔드 상세 정보 저장
        timestamp: timestamp,
        resourceSummary: resourceSummary,
        importantInfo: importantInfo,
      }));
    }
  };

  // 명예의전당 보고서 조회 핸들러
  const handleViewHallOfFameReport = async (siteId: number, url: string, siteName: string) => {
    try {
      console.log(`📊 명예의전당 보고서 요청: siteId=${siteId}, url=${url}`);

      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3300';
      const response = await fetch(`${BACKEND_URL}/api/hall-of-fame/report/${siteId}?includeLLM=true`);

      if (!response.ok) {
        throw new Error('보고서를 불러올 수 없습니다.');
      }

      const result = await response.json();

      if (result.success && result.analysis) {
        const { pageSpeedData, llmAnalysis, url: reportUrl, timestamp, resourceSummary, importantInfo, customRatings } = result.analysis;

        console.log(`✅ 명예의전당 보고서 로드 성공:`, siteName);
        console.log(`📊 커스텀 성능 점수:`, customRatings);

        setState(prev => ({
          ...prev,
          isLoading: false,
          data: [], // 서비스 데이터는 없음
          pageSpeedData: pageSpeedData,
          llmAnalysis: llmAnalysis,
          analyzedUrl: reportUrl,
          currentUrl: reportUrl,
          error: null,
          isBatchMode: false,
          // 백엔드 상세 정보 (있는 경우)
          timestamp: timestamp,
          resourceSummary: resourceSummary,
          importantInfo: importantInfo,
          customRatings: customRatings // V1.1 커스텀 성능 점수
        }));
      } else {
        throw new Error(result.error || '보고서 형식이 올바르지 않습니다.');
      }
    } catch (error: any) {
      console.error('❌ 명예의전당 보고서 로드 실패:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: `보고서를 불러올 수 없습니다: ${error.message}`
      }));
    }
  };

  const handleReset = () => {
    setState({
      isLoading: false,
      isLlmLoading: false,
      data: null,
      pageSpeedData: null,
      llmAnalysis: null,
      error: null,
      analyzedUrl: null,
      currentStep: 0,
      totalSteps: 3,
      stepName: '',
      sessionId: null,
      // 다중 URL 분석 상태도 초기화
      isBatchMode: false,
      batchId: null,
      batchResults: [],
      selectedUrlIndex: undefined,
      multiProgress: {
        completed: 0,
        total: 0,
        currentUrl: undefined,
        currentIndex: undefined,
      },
      // 백엔드 상세 정보 초기화
      timestamp: undefined,
      resourceSummary: undefined,
      importantInfo: undefined,
    });
    disconnectWebSocket();
  };

  // URL 파라미터로 대시보드 버전 및 보고서 타입 확인
  const urlParams = new URLSearchParams(window.location.search);
  const reportType = urlParams.get('report');

  // 프로덕션 환경에서는 항상 V2 사용 (완전 전환)
  // 개발 환경에서는 ?legacy=true로만 레거시 접근 가능
  const isProduction = process.env.NODE_ENV === 'production';
  const useLegacy = !isProduction && urlParams.get('legacy') === 'true';
  const isV2Report = urlParams.get('v2') === 'true'; // 보고서 페이지용

  // 명예의전당 보고서 페이지 (인트로 화면 없이 바로 표시)
  if (reportType === 'hall-of-fame') {
    // V2 명예의전당 보고서 (프로덕션은 항상 V2, 개발은 ?v2=true 필요)
    return (isProduction || isV2Report) ? <HallOfFameReportV2 /> : <HallOfFameReport />;
  }

  // 인트로 화면 표시
  if (shouldShowIntro) {
    return <IntroPage onGuestMode={enterGuestMode} />;
  }

  // 프로덕션 환경이거나 레거시 모드가 아니면 V2 사용 (기본)
  if (isProduction || !useLegacy) {
    return <AppV2 />;
  }

  return (
    <div className="app">
      <div className="container">
        <div className="header">
          <h1>🌍 사이트 성능 분석</h1>
          <p>쇼핑몰에서 사용 중인 서비스들을 분석하고 시각화합니다</p>
          
          {/* 사용자 상태 표시 */}
          <div style={{
            textAlign: 'right',
            marginTop: '10px',
            fontSize: '14px',
            color: '#666'
          }}>
            {user ? (
              <span>👋 {user.name} ({user.email})</span>
            ) : (
              <span>👤 게스트 모드</span>
            )}
          </div>
        </div>
        <URLInput
          onAnalyze={handleAnalyze}
          onMultiAnalyze={handleMultiAnalyze}
          isLoading={state.isLoading}
        />

        {/* 저장된 분석 불러오기 (Dashboard가 없을 때만 표시) */}
        {canLoadAnalysis && !state.analyzedUrl && (
          <div style={{
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <button
              onClick={() => setShowSavedAnalysesModal(true)}
              style={{
                padding: '12px 24px',
                backgroundColor: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#4f46e5';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#6366f1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              📂 저장된 분석 불러오기
            </button>
          </div>
        )}

        {state.error && (
          <div className="error">
            <h3>❌ 오류가 발생했습니다</h3>
            <p>{state.error}</p>
            <button className="analyze-btn" onClick={handleReset}>다시 시도</button>
          </div>
        )}

        {state.isLoading && (
          <>
            {state.isBatchMode ? (
              // 다중 URL 분석 진행 상황
              <MultiUrlProgress
                urls={state.multiProgress.urls || []}
                currentIndex={state.multiProgress.currentIndex}
                completed={state.multiProgress.completed}
                total={state.multiProgress.total}
                urlProgress={state.multiProgress.urlProgress}
                sessionId={state.sessionId || undefined}
                onUrlClick={handleUrlClick}
                batchResults={state.batchResults}
              />
            ) : (
              // 단일 URL 분석 진행 상황
              <AnalysisProgress
                currentStep={state.currentStep}
                totalSteps={state.totalSteps}
                stepName={state.stepName}
                sessionId={state.sessionId}
              />
            )}
          </>
        )}

        {/* 다중 URL 분석 결과 표시 */}
        {state.isBatchMode && state.batchResults.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '30px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(5, 150, 105, 0.2)'
          }}>
            <h2 style={{
              color: '#059669',
              marginBottom: '25px',
              fontSize: '20px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              🎉 동시 페이지 분석 완료! ({state.batchResults.length}개 사이트)
            </h2>

            <div style={{
              display: 'grid',
              gap: '20px'
            }}>
              {state.batchResults.map((result, index) => {
                const isClickable = !result.failed;
                return (
                <div
                  key={index}
                  style={{
                    position: 'relative',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '16px', // 20px → 16px로 축소
                    backgroundColor: result.failed ? '#fef2f2' : '#f8fafc',
                    borderColor: result.failed ? '#fca5a5' : (isClickable ? '#3b82f6' : '#e5e7eb'),
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'all 0.3s ease',
                    boxShadow: isClickable ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isClickable) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.15)';
                    }
                  }}
                  onClick={isClickable ? () => {
                    console.log(`🔗 URL 카드 클릭: ${result.url} (인덱스: ${index})`);
                    handleUrlClick(result.url, index);
                  } : undefined}
                >
                  {/* URL 헤더 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px' // 15px → 12px로 축소
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: 1
                    }}>
                      <h3 style={{
                        color: result.failed ? '#dc2626' : '#374151',
                        fontSize: '16px',
                        fontWeight: '600',
                        margin: 0,
                        wordBreak: 'break-all'
                      }}>
                        {index + 1}. {result.url}
                      </h3>

                      {/* 클릭 안내 버튼 - URL 바로 옆 */}
                      {isClickable && (
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: '#dbeafe',
                          color: '#3b82f6',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap'
                        }}>
                          클릭하여 보고서 보기
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{
                        backgroundColor: result.failed ? '#dc2626' : '#10b981',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {result.failed ? '실패' : '성공'}
                      </span>
                    </div>
                  </div>

                  {result.failed ? (
                    // 실패한 경우 에러 메시지 표시
                    <div style={{
                      color: '#dc2626',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}>
                      ❌ 오류: {result.error || '알 수 없는 오류가 발생했습니다.'}
                    </div>
                  ) : (
                    // 성공한 경우 - 정보 표시 제거
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      분석이 완료되었습니다. 클릭하여 보고서를 확인하세요.
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* 다중 URL 분석 액션 버튼들 */}
            <div style={{
              textAlign: 'center',
              marginTop: '25px',
              paddingTop: '20px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>

                <button
                  onClick={handleReset}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b7280';
                  }}
                >
                  🔄 새로운 분석 시작
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 분석 결과 표시 (단일/다중 URL 모두) */}
        {state.analyzedUrl && (state.pageSpeedData || state.llmAnalysis || state.data) && (
          <>
            {/* 저장/불러오기 기능 버튼 */}
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              padding: '20px',
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
            }}>
              <div style={{ marginBottom: '16px', color: '#334155', fontWeight: '600', fontSize: '16px' }}>
                💾 분석 결과 관리
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* 저장 버튼 (로그인한 사용자만) */}
                {canSaveAnalysis && (
                  <SaveAnalysisButton
                    url={state.analyzedUrl}
                    pageSpeedData={state.pageSpeedData}
                    llmAnalysis={state.llmAnalysis}
                  />
                )}
                
                {/* 불러오기 버튼 (로그인한 사용자만) */}
                {canLoadAnalysis && (
                  <button
                    onClick={() => setShowSavedAnalysesModal(true)}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: '#6366f1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#4f46e5';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#6366f1';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    📂 저장된 분석 불러오기
                  </button>
                )}
                
                {/* 게스트 모드일 때 로그인 안내 */}
                {isGuestMode && (
                  <div style={{
                    padding: '12px 20px',
                    backgroundColor: '#f8fafc',
                    color: '#64748b',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🔒 로그인하시면 분석 결과를 저장하고 관리할 수 있습니다
                  </div>
                )}
              </div>
            </div>

            {/* 다중 URL 분석의 경우 탭 인터페이스 표시 */}
            {state.isBatchMode && state.batchResults.length > 0 && (
              <div style={{
                marginBottom: '20px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: '4px'
                }}>
                  {state.batchResults.map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleUrlClick(result.url || '', index)}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        borderBottom: `3px solid ${(state.selectedUrlIndex ?? 0) === index ? '#3b82f6' : 'transparent'}`,
                        backgroundColor: (state.selectedUrlIndex ?? 0) === index ? '#eff6ff' : 'transparent',
                        color: (state.selectedUrlIndex ?? 0) === index ? '#1d4ed8' : '#6b7280',
                        fontSize: '14px',
                        fontWeight: (state.selectedUrlIndex ?? 0) === index ? '600' : '400',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        borderRadius: '6px 6px 0 0',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseOver={(e) => {
                        if ((state.selectedUrlIndex ?? 0) !== index) {
                          e.currentTarget.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseOut={(e) => {
                        if ((state.selectedUrlIndex ?? 0) !== index) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      {index + 1}. {result.url ? new URL(result.url).hostname : `URL ${index + 1}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 레거시 대시보드 렌더링 (개발 환경 테스트용) */}
            <Dashboard
              data={state.data || []}
              url={state.analyzedUrl}
              pageSpeedData={state.pageSpeedData}
              llmAnalysis={state.llmAnalysis}
              isLlmLoading={state.isLlmLoading}
              batchResults={state.batchResults}
              selectedUrlIndex={state.selectedUrlIndex}
              onUrlChange={handleUrlClick}
              onViewHallOfFameReport={handleViewHallOfFameReport}
              timestamp={state.timestamp}
              resourceSummary={state.resourceSummary}
              importantInfo={state.importantInfo}
              customRatings={state.customRatings}
            />
            
            {/* 게스트 모드 안내 메시지 */}
            {isGuestMode && state.analyzedUrl && (state.pageSpeedData || state.llmAnalysis) && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '30px', 
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f0f9ff',
                borderRadius: '16px',
                border: '2px solid #0ea5e9'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: '#0c4a6e', 
                    fontSize: '18px', 
                    fontWeight: '600' 
                  }}>
                    👤 게스트 모드로 이용 중
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    color: '#075985', 
                    fontSize: '14px' 
                  }}>
                    분석 결과를 저장하려면 Google 계정으로 로그인해주세요
                  </p>
                </div>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#0ea5e9',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  🔐 로그인하러 가기
                </button>
              </div>
            )}

            {/* 저장 버튼 (로그인한 사용자만) */}
            {canSaveAnalysis && state.analyzedUrl && (state.pageSpeedData || state.llmAnalysis) && (
              <div style={{ 
                textAlign: 'center', 
                marginTop: '30px', 
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: '#f8fafc',
                borderRadius: '16px',
                border: '2px solid #e2e8f0'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: '#1e293b', 
                    fontSize: '18px', 
                    fontWeight: '600' 
                  }}>
                    💾 분석 결과 저장
                  </h3>
                  <p style={{ 
                    margin: 0, 
                    color: '#64748b', 
                    fontSize: '14px' 
                  }}>
                    이 분석 결과를 저장하여 나중에 다시 확인할 수 있습니다
                  </p>
                </div>
                <SaveAnalysisButton
                  url={state.analyzedUrl}
                  pageSpeedData={state.pageSpeedData}
                  llmAnalysis={state.llmAnalysis}
                />
              </div>
            )}
            
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button className="analyze-btn" onClick={handleReset}>새로운 분석 시작</button>
            </div>
          </>
        )}

      </div>
      
      {/* 저장된 분석 목록 모달 (로그인한 사용자만) */}
      {canLoadAnalysis && (
        <SavedAnalysesModal
          isOpen={showSavedAnalysesModal}
          onClose={() => setShowSavedAnalysesModal(false)}
          onLoadAnalysis={handleLoadAnalysis}
        />
      )}
      
      {/* 관리자 로그인 */}
      <AdminLogin />
    </div>
  );
}

// AuthProvider로 감싼 메인 App 컴포넌트
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App; 